import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import ColorPicker from './ColorPicker';
import { DeleteIcon } from '../Icons';

const formatPercent = (value) => `${Math.max(0, Math.min(100, value)).toFixed(3)}%`;

const LabelColorSlider = ({ minValue, maxValue, stops = [], setStops, colorGrading = true }) => {
    const barRef = useRef(null);
    const [draggingIdx, setDraggingIdx] = useState(null);
    const [hoverData, setHoverData] = useState({ visible: false, value: 0, x: 0 });
    const [editingIdx, setEditingIdx] = useState(null);
    const [draftValues, setDraftValues] = useState(() =>
        stops.map((stop) => String(stop?.value ?? ''))
    );

    const min = parseFloat(minValue) || 0;
    const max = parseFloat(maxValue) || 100;
    const range = max - min;
    const safeRange = range === 0 ? 1 : range;
    const sortedStops = useMemo(
        () => [...stops].sort((left, right) => Number(left?.value || 0) - Number(right?.value || 0)),
        [stops]
    );

    useEffect(() => {
        setDraftValues((previousDrafts) => stops.map((stop, index) => {
            if (editingIdx === index && previousDrafts[index] !== undefined) {
                return previousDrafts[index];
            }

            return String(stop?.value ?? '');
        }));
    }, [editingIdx, stops]);

    const normalizeStopValue = useCallback((rawValue, fallbackValue) => {
        const parsed = parseFloat(rawValue);
        if (!Number.isFinite(parsed)) {
            return fallbackValue;
        }

        const lowerBound = Math.min(min, max);
        const upperBound = Math.max(min, max);
        return Math.min(upperBound, Math.max(lowerBound, parsed));
    }, [max, min]);

    const getValueFromX = useCallback((clientX) => {
        if (!barRef.current) return 0;
        const rect = barRef.current.getBoundingClientRect();
        const offsetX = clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, offsetX / rect.width));
        return Math.round(min + (percentage * range));
    }, [min, range]);

    const handleBarClick = (e) => {
        if (draggingIdx !== null) return;
        const newValue = getValueFromX(e.clientX);
        if (stops.some(s => Math.abs(s.value - newValue) < (safeRange * 0.02))) return;

        const newStop = { value: newValue, color: '#01ABB4' };
        const updatedStops = [...stops, newStop].sort((a, b) => a.value - b.value);
        setStops(updatedStops);
    };

    const handleMouseDown = (e, index) => {
        e.stopPropagation();
        setDraggingIdx(index);
    };

    const handleMouseMove = useCallback((e) => {
        if (draggingIdx === null) return;
        const newValue = getValueFromX(e.clientX);
        const newStops = stops.map((stop, index) => (
            index === draggingIdx ? { ...stop, value: newValue } : stop
        ));
        setStops(newStops);
    }, [draggingIdx, stops, getValueFromX, setStops]);

    const handleMouseUp = useCallback(() => {
        if (draggingIdx !== null) {
            const sortedStops = [...stops].sort((a, b) => a.value - b.value);
            setStops(sortedStops);
            setDraggingIdx(null);
        }
    }, [draggingIdx, stops, setStops]);

    const handleHoverMove = (e) => {
        if (!barRef.current || draggingIdx !== null) return;
        const rect = barRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const val = getValueFromX(e.clientX);
        setHoverData({ visible: true, value: val, x: (x / rect.width) * 100 });
    };

    useEffect(() => {
        if (draggingIdx !== null) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [draggingIdx, handleMouseMove, handleMouseUp]);

    const updateStopColor = (index, color) => {
        setStops(
            stops.map((stop, stopIndex) => (
                stopIndex === index ? { ...stop, color } : stop
            ))
        );
    };

    const commitDraftValue = useCallback((index) => {
        const currentStop = stops[index];
        if (!currentStop) {
            setEditingIdx((current) => (current === index ? null : current));
            return;
        }

        const fallbackValue = Number.isFinite(Number(currentStop.value))
            ? Number(currentStop.value)
            : min;
        const nextValue = normalizeStopValue(draftValues[index], fallbackValue);

        setDraftValues((previousDrafts) => {
            const nextDrafts = [...previousDrafts];
            nextDrafts[index] = String(nextValue);
            return nextDrafts;
        });

        const nextStops = stops.map((stop, stopIndex) => (
            stopIndex === index ? { ...stop, value: nextValue } : stop
        ));

        setStops([...nextStops].sort((a, b) => a.value - b.value));
        setEditingIdx((current) => (current === index ? null : current));
    }, [draftValues, min, normalizeStopValue, setStops, stops]);

    const resetDraftValue = useCallback((index) => {
        setDraftValues((previousDrafts) => {
            const nextDrafts = [...previousDrafts];
            nextDrafts[index] = String(stops[index]?.value ?? '');
            return nextDrafts;
        });
        setEditingIdx((current) => (current === index ? null : current));
    }, [stops]);

    const removeStop = (index) => {
        setStops(stops.filter((_, i) => i !== index));
    };

    const trackBackground = useMemo(() => {
        if (sortedStops.length === 0) {
            return '#555';
        }

        if (sortedStops.length === 1) {
            return sortedStops[0]?.color || '#555';
        }

        const percentForValue = (value) => ((Number(value) - min) / safeRange) * 100;

        if (colorGrading) {
            return `linear-gradient(to right, ${sortedStops
                .map((stop) => `${stop.color} ${formatPercent(percentForValue(stop.value))}`)
                .join(', ')})`;
        }

        const firstStop = sortedStops[0];
        const segments = [
            `${firstStop.color} 0%`,
            `${firstStop.color} ${formatPercent(percentForValue(firstStop.value))}`,
        ];

        for (let index = 1; index < sortedStops.length; index += 1) {
            const previousStop = sortedStops[index - 1];
            const currentStop = sortedStops[index];
            const previousPercent = formatPercent(percentForValue(previousStop.value));
            const currentPercent = formatPercent(percentForValue(currentStop.value));

            segments.push(`${currentStop.color} ${previousPercent}`);
            segments.push(`${currentStop.color} ${currentPercent}`);
        }

        const lastStop = sortedStops[sortedStops.length - 1];
        segments.push(`${lastStop.color} 100%`);

        return `linear-gradient(to right, ${segments.join(', ')})`;
    }, [colorGrading, min, safeRange, sortedStops]);

    return (
        <div className="color-intervals-manager" style={{ marginTop: '20px', padding: '15px', background: 'rgba(13, 30, 54, 0.4)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <label className="form-lbl" style={{ marginBottom: '25px', display: 'block', opacity: 0.8, fontSize: '11px', letterSpacing: '1px', fontWeight: 'bold' }}>
                {colorGrading ? 'GRADIENT TIMELINE' : 'STEP TIMELINE'}
            </label>
            
            <div style={{ position: 'relative', padding: '0 10px', marginBottom: '45px' }}>
                <div 
                    ref={barRef}
                    onClick={handleBarClick}
                    onMouseMove={handleHoverMove}
                    onMouseLeave={() => setHoverData({ ...hoverData, visible: false })}
                    style={{ 
                        height: '16px', 
                        width: '100%', 
                        borderRadius: '8px', 
                        background: trackBackground,
                        position: 'relative',
                        cursor: 'crosshair',
                        boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.6)',
                    }}
                >
                    {hoverData.visible && (
                        <div style={{
                            position: 'absolute',
                            left: `${hoverData.x}%`,
                            top: '-35px',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'var(--accent)',
                            color: '#000',
                            padding: '2px 8px',
                            borderRadius: '5px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            pointerEvents: 'none',
                            zIndex: 15,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.5)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            {hoverData.value}
                            <div style={{ width: '1px', height: '35px', background: 'var(--accent)', marginTop: '2px', opacity: 0.6 }} />
                        </div>
                    )}

                    {stops.map((stop, i) => {
                        const pos = ((stop.value - min) / safeRange) * 100;
                        return (
                            <div 
                                key={`${stop.value}-${stop.color}-${i}`}
                                onMouseDown={(e) => handleMouseDown(e, i)}
                                style={{
                                    position: 'absolute',
                                    left: `${pos}%`,
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: '#fff',
                                    borderRadius: '50%',
                                    cursor: 'ew-resize',
                                    border: `3px solid ${stop.color}`,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.8)',
                                    zIndex: draggingIdx === i ? 20 : 5,
                                    transition: draggingIdx === i ? 'none' : 'left 0.1s ease-out'
                                }}
                            >
                                <div style={{
                                    position: 'absolute',
                                    bottom: '22px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    color: '#000',
                                    padding: '1px 5px',
                                    borderRadius: '3px',
                                    fontSize: '9px',
                                    fontWeight: 'bold',
                                    pointerEvents: 'none'
                                }}>
                                    {stop.value}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {stops.map((stop, index) => (
                    <div key={`${stop.value}-${stop.color}-${index}`} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '15px', 
                        background: draggingIdx === index ? 'rgba(1, 171, 180, 0.15)' : 'rgba(255,255,255,0.02)', 
                        padding: '10px 15px', 
                        borderRadius: '8px',
                        border: draggingIdx === index ? '1px solid #01ABB4' : '1px solid rgba(255,255,255,0.05)',
                        transition: 'all 0.2s ease',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}>
                        <span style={{ fontSize: '12px', opacity: 0.4, fontWeight: 'bold', minWidth: '20px' }}>#{index + 1}</span>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <span style={{ fontSize: '11px', opacity: 0.5 }}>Limit:</span>
                            <input 
                                type="number" 
                                className="add-column" 
                                style={{ 
                                    flex: 1, 
                                    padding: '5px 10px', 
                                    fontSize: '13px', 
                                    background: 'rgba(0,0,0,0.2)', 
                                    border: '1px solid rgba(255,255,255,0.1)', 
                                    color: '#fff',
                                    borderRadius: '4px'
                                }}
                                value={draftValues[index] ?? String(stop?.value ?? '')}
                                onFocus={() => setEditingIdx(index)}
                                onChange={(e) => {
                                    const nextDraft = e.target.value;
                                    setDraftValues((previousDrafts) => {
                                        const nextDrafts = [...previousDrafts];
                                        nextDrafts[index] = nextDraft;
                                        return nextDrafts;
                                    });
                                }}
                                onBlur={() => commitDraftValue(index)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.currentTarget.blur();
                                    }

                                    if (e.key === 'Escape') {
                                        e.preventDefault();
                                        resetDraftValue(index);
                                        e.currentTarget.blur();
                                    }
                                }}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <ColorPicker id={`stop-${index}`} color={stop.color} setColor={(c) => updateStopColor(index, c)} />
                            <button 
                                className="details-delete-btn" 
                                style={{ position: 'static', padding: '6px', background: 'rgba(236, 69, 78, 0.1)', borderRadius: '6px' }} 
                                onClick={(e) => { e.stopPropagation(); removeStop(index); }}
                            >
                                <DeleteIcon color="var(--red)" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {stops.length === 0 && (
                <p style={{ textAlign: 'center', fontSize: '12px', opacity: 0.3, padding: '20px', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                    Click on the gradient track above to add a color point.
                </p>
            )}
        </div>
    );
};

export default LabelColorSlider;
